import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, Stack, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { snackbarService } from 'services/snackbarService';
import { RawProductIngredient } from 'services/rawProductService';
import { usePreviewRawProductIngredients } from 'hooks/useRawProductCategoryReview';
import { usePressToSave } from './usePressToSave';

interface Props {
  rawProductId: string;
  // The photos ticked on the card for this reading. Empty is the whole strip.
  mediaIds: string[];
  ingredients: RawProductIngredient[] | null;
  open: boolean;
  productName: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (ingredients: RawProductIngredient[]) => void;
}

// Aliases and components are edited one per line rather than comma-separated:
// INCI names carry commas of their own ("1,4-Butanediol"), so a comma cannot be
// what splits them.
interface IngredientDraft {
  name: string;
  asPrinted: string;
  aliases: string;
  components: string;
}

function toDrafts(ingredients: RawProductIngredient[] | null): IngredientDraft[] {
  return (ingredients ?? []).map((ingredient) => ({
    name: ingredient.name,
    asPrinted: ingredient.asPrinted,
    aliases: ingredient.aliases.join('\n'),
    components: ingredient.components.join('\n'),
  }));
}

const lines = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

export default function IngredientsModal({
  rawProductId,
  mediaIds,
  ingredients,
  open,
  productName,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [drafts, setDrafts] = useState<IngredientDraft[]>([]);
  const { saving, press } = usePressToSave(isSaving, open);

  const { mutate: preview, isLoading: isAnalyzing } = usePreviewRawProductIngredients(
    (result) => {
      setDrafts(toDrafts(result.ingredients));
      snackbarService.showSnackbar(
        result.ingredients.length
          ? `Read ${result.ingredients.length} ingredients off the panel - check them, then save`
          : 'No panel could be read off this pack',
        result.ingredients.length ? 'success' : 'info',
      );
    },
    () => snackbarService.showSnackbar('Could not read the ingredients panel', 'error'),
  );

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned reading that came back on the next open would look
  // like a stored one.
  useEffect(() => {
    if (!open) return;
    setDrafts(toDrafts(ingredients));
  }, [open, ingredients]);

  const busy = saving || isAnalyzing;
  const changed = JSON.stringify(drafts) !== JSON.stringify(toDrafts(ingredients));

  const patch = (row: number, next: Partial<IngredientDraft>) =>
    setDrafts((existing) =>
      existing.map((draft, position) => (position === row ? { ...draft, ...next } : draft)),
    );

  return (
    <Modal open={open} onClose={busy ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 1100,
          maxWidth: '94vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <CustomText text="Ingredients" fontWeight={700} fontSize={18} />
            <Box mt={0.5}>
              <CustomText text={productName} fontSize={13} color={colors.gray} />
            </Box>
          </Box>
          <CustomButton
            text="Analyze"
            height={36}
            isLoading={isAnalyzing}
            disabled={saving}
            onClick={() => preview({ rawProductId, mediaIds })}
          />
        </Stack>

        <Box mt={1}>
          <CustomText
            text={
              mediaIds.length
                ? `Analyze reads the panel from the ${mediaIds.length} ticked photos. Nothing is stored until you save.`
                : 'Analyze reads the panel from every photo. Nothing is stored until you save.'
            }
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        {drafts.length === 0 && (
          <Box mt={2}>
            <CustomText
              text="No ingredients. Saving now records that no panel could be read off this pack."
              fontSize={13}
              color={colors.gray}
            />
          </Box>
        )}

        {drafts.map((draft, row) => (
          <Box
            key={`ingredient-${row}`}
            mt={1.5}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr auto' },
              columnGap: 1.5,
              rowGap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Stack spacing={0.5}>
              <CustomText text={`${row + 1}. Name (INCI)`} fontSize={11} fontWeight={600} color={colors.gray} />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.name}
                disabled={busy}
                onChange={(event) => patch(row, { name: event.target.value })}
                placeholder="—"
              />
            </Stack>
            <Stack spacing={0.5}>
              <CustomText text="As printed" fontSize={11} fontWeight={600} color={colors.gray} />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.asPrinted}
                disabled={busy}
                onChange={(event) => patch(row, { asPrinted: event.target.value })}
                placeholder="—"
              />
            </Stack>
            <Stack spacing={0.5}>
              <CustomText text="Aliases (one per line)" fontSize={11} fontWeight={600} color={colors.gray} />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.aliases}
                disabled={busy}
                onChange={(event) => patch(row, { aliases: event.target.value })}
                placeholder="—"
              />
            </Stack>
            <Stack spacing={0.5}>
              <CustomText text="Components (one per line)" fontSize={11} fontWeight={600} color={colors.gray} />
              <TextField
                fullWidth
                size="small"
                multiline
                value={draft.components}
                disabled={busy}
                onChange={(event) => patch(row, { components: event.target.value })}
                placeholder="—"
              />
            </Stack>
            <Tooltip title="Remove this ingredient">
              <IconButton
                size="small"
                disabled={busy}
                sx={{ mt: 2.25 }}
                onClick={() =>
                  setDrafts((existing) => existing.filter((_draft, position) => position !== row))
                }
              >
                <DeleteOutlineIcon sx={{ fontSize: 16, color: colors.gray }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Box mt={2}>
          <CustomButton
            text="Add ingredient"
            variant="outlined"
            height={32}
            icon={<AddIcon sx={{ fontSize: 16 }} />}
            disabled={busy}
            onClick={() =>
              setDrafts((existing) => [...existing, { name: '', asPrinted: '', aliases: '', components: '' }])
            }
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={busy}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={saving}
            disabled={!changed || isAnalyzing}
            onClick={() => press(() =>
              onSave(
                drafts
                  // A row with no name is one the reviewer emptied rather than
                  // one they wrote.
                  .filter((draft) => draft.name.trim())
                  .map((draft) => ({
                    name: draft.name.trim(),
                    asPrinted: draft.asPrinted.trim() || draft.name.trim(),
                    aliases: lines(draft.aliases),
                    components: lines(draft.components),
                  })),
              ),
            )}
          />
        </Stack>
      </Box>
    </Modal>
  );
}
